import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ArrowLeft, Save, Upload, File, Package, Plus, X, Search, Check, ChevronsUpDown, Trash2, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { getOrderByCode, updateOrder } from "@/integrations/supabase/orders";
import { uploadOrderFile } from "@/integrations/supabase/storage";
import { getProducts } from "@/integrations/supabase/products";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSync } from "@/contexts/SyncContext";
import { useSyncOperations } from "@/hooks/useSyncOperations";
import { useOrderStatusConfig } from "@/hooks/useOrderStatusConfig";
import { PersonalizationListEditor, PersonalizationEntry, createEmptyPersonalization } from "@/components/PersonalizationListEditor";
import { CLOTHING_SIZES } from "@/constants/sizes";
import { cn } from "@/lib/utils";
import { useClothingSizes } from "@/hooks/useClothingSizes";
import { AddCustomSizeDialog } from "@/components/AddCustomSizeDialog";
import { useAuth } from "@/components/AuthProvider";
import { buscarConfiguracao, gerarDatasParcelamento } from "@/integrations/supabase/payment-installments";
import ArquivosSection from "@/components/ArquivosSection";
import { getArquivos, ArquivoData } from "@/integrations/supabase/arquivos";

// Função auxiliar para gerar IDs únicos
const generateId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const parseCatalogProducts = (catalogProducts: unknown) => {
  if (!catalogProducts) return [];
  if (Array.isArray(catalogProducts)) return catalogProducts;
  if (typeof catalogProducts === "string") {
    try {
      const parsed = JSON.parse(catalogProducts);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const parseKitFromDescription = (description: string) => {
  const kitLine = description.split("\n").find((line) => line.trim().startsWith("Kit:"));
  const totalLine = description.split("\n").find((line) => line.trim().startsWith("Total:"));

  if (!kitLine) {
    return { isKit: false, kitItems: [] as Array<{ id: string; size: string; quantity: number }>, color: "" };
  }

  const kitInfo = kitLine.replace(/^Kit:\s*/i, "").trim();
  const items = kitInfo
    .split("|")
    .map((part) => part.trim())
    .map((part) => {
      const match = part.match(/(\d+)\s+(.+)/i);
      if (!match) return null;
      return {
        id: generateId(),
        quantity: Number(match[1]),
        size: match[2].trim(),
      };
    })
    .filter(Boolean) as Array<{ id: string; size: string; quantity: number }>;

  const colorMatch = totalLine?.match(/\|\s*Cor:\s*([^|]+)/i);
  const color = colorMatch ? colorMatch[1].trim() : "";

  return { isKit: items.length > 0, kitItems: items, color };
};

export default function EditarPedido() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { invalidateRelated } = useSync();
  const { syncAfterUpdate } = useSyncOperations();
  const { empresa } = useAuth();
  const { sizes: clothingSizes, addSize: addCustomSize, isLoading: isSizesLoading } = useClothingSizes(empresa?.id);
  
  const [client, setClient] = useState("");
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [value, setValue] = useState<number>(0);
  const [discount, setDiscount] = useState<number>(0);
  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');
  const [paid, setPaid] = useState<number>(0);
  const [delivery, setDelivery] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [status, setStatus] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [currentFileUrl, setCurrentFileUrl] = useState<string | null>(null);
  const [personalizations, setPersonalizations] = useState<PersonalizationEntry[]>([]);
  const [quantity, setQuantity] = useState<number>(1);
  const [size, setSize] = useState<string>("");
  const [color, setColor] = useState<string>("");
  const [isKitMode, setIsKitMode] = useState<boolean>(false);
  const [kitItems, setKitItems] = useState<Array<{ id: string; size: string; quantity: number }>>([
    { id: generateId(), size: "P", quantity: 0 },
    { id: generateId(), size: "M", quantity: 0 },
    { id: generateId(), size: "G", quantity: 0 },
    { id: generateId(), size: "GG", quantity: 0 },
  ]);
  
  // Estados para forma de pagamento
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [cardFeePercent, setCardFeePercent] = useState<number>(0);
  const [installmentConfig, setInstallmentConfig] = useState<any>(null);
  const [selectedInstallments, setSelectedInstallments] = useState<number>(1);
  const [installmentDates, setInstallmentDates] = useState<string[]>([]);
  const [paymentConditions, setPaymentConditions] = useState<string>("");
  const [arquivos, setArquivos] = useState<ArquivoData[]>([]);
  const [isLoadingArquivos, setIsLoadingArquivos] = useState(false);

  // Estados para produtos do catálogo
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [productSearchTerm, setProductSearchTerm] = useState<string>("");
  const [productPopoverOpen, setProductPopoverOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addedCatalogProducts, setAddedCatalogProducts] = useState<Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    fiscal_data?: {
      ncm_code?: string;
      cest_code?: string;
      icms_origem?: string;
      icms_aliquota?: number;
      pis_aliquota?: number;
      cofins_aliquota?: number;
      ipi_aliquota?: number;
    };
  }>>([]);

  const { statusOptions } = useOrderStatusConfig();
  
  // Buscar produtos do catálogo
  const { data: products = [] } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });
  
  // Normalizar texto para busca (remove acentos, espaços extras, caixa)
  const normalizeSearch = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .replace(/\s+/g, " ");

  // Filtrar produtos baseado no termo de busca
  const filteredProducts = useMemo(() => {
    if (!productSearchTerm) return products;
    const normalized = normalizeSearch(productSearchTerm);
    return products.filter((product) => {
      const nameMatch = normalizeSearch(product.name || "").includes(normalized);
      const typeMatch = product.type && normalizeSearch(product.type).includes(normalized);
      const materialsMatch = Array.isArray(product.materials)
        ? product.materials.some((m: string) => normalizeSearch(m).includes(normalized))
        : false;
      return nameMatch || typeMatch || materialsMatch;
    });
  }, [products, productSearchTerm]);
  
  // Atualizar valor total quando produtos adicionados mudarem
  useEffect(() => {
    if (addedCatalogProducts.length > 0) {
      const totalValue = addedCatalogProducts.reduce((sum, product) => 
        sum + (product.price * product.quantity), 0
      );
      setValue(totalValue);
    }
  }, [addedCatalogProducts]);

  const addKitSize = () => {
    setKitItems([...kitItems, { id: generateId(), size: "", quantity: 0 }]);
  };

  const removeKitSize = (id: string) => {
    setKitItems(kitItems.filter((item) => item.id !== id));
  };

  const updateKitSize = (id: string, size: string) => {
    const updated = kitItems.map((item) => (item.id === id ? { ...item, size } : item));
    setKitItems(updated);
  };

  const updateKitQuantity = (id: string, quantity: number) => {
    const updated = kitItems.map((item) => (item.id === id ? { ...item, quantity } : item));
    setKitItems(updated);
  };

  const getTotalKitQuantity = () => {
    return kitItems.reduce((total, item) => total + item.quantity, 0);
  };

  const { data: order, isLoading } = useQuery({
    queryKey: ["order", id],
    queryFn: () => getOrderByCode(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (order) {
      setClient(order.customer_name || "");
      setType(order.type || "");
      setDescription(order.description || "");
      setValue(order.value || 0);
      setDiscount((order as any)?.discount || 0);
      setDiscountType((order as any)?.discount_type || 'fixed');
      setPaid(order.paid || 0);
      setDelivery(order.delivery_date || "");
      setDueDate((order as any)?.due_date || "");
      setPaymentMethod((order as any)?.payment_method || "");
      setStatus(order.status || "");
      setCurrentFileUrl(order.file_url || null);
  setQuantity(1);
      const catalogProducts = parseCatalogProducts((order as any).catalog_products);
      if (catalogProducts.length > 0) {
        setAddedCatalogProducts(catalogProducts as any);
        if (!order.type || order.type === "") {
          setType("catalogo");
        }
      }
      setPersonalizations(
        (order.personalizations ?? []).map((item) => ({
          id: item.id ?? createEmptyPersonalization().id,
          personName: item.person_name ?? "",
          size: item.size ?? "",
          quantity: item.quantity ?? 1,
          notes: item.notes ?? "",
        }))
      );

      if (order.description) {
        const parsedKit = parseKitFromDescription(order.description);
        if (parsedKit.isKit) {
          setIsKitMode(true);
          setKitItems(parsedKit.kitItems);
          setColor(parsedKit.color);
        } else {
          setIsKitMode(false);
        }
      }

      // Carregar condições de pagamento das observações se existirem
      if (order.observations) {
        const paymentConditionsMatch = order.observations.match(/📋 CONDIÇÕES DE PAGAMENTO:\n([\s\S]*?)(?:\n\n|$)/);
        if (paymentConditionsMatch && paymentConditionsMatch[1]) {
          setPaymentConditions(paymentConditionsMatch[1].trim());
        }
      }
    }
  }, [order]);

  // Carregar arquivos quando o pedido é carregado
  useEffect(() => {
    if (!id) return;

    const loadArquivos = async () => {
      setIsLoadingArquivos(true);
      try {
        const arquivosCarregados = await getArquivos('orders', id);
        setArquivos(arquivosCarregados);
      } catch (error) {
        console.error("Erro ao carregar arquivos:", error);
      } finally {
        setIsLoadingArquivos(false);
      }
    };

    loadArquivos();
  }, [id]);

  // Buscar configuração de parcelamento quando forma de pagamento mudar
  useEffect(() => {
    const loadInstallmentConfig = async () => {
      if (!paymentMethod) {
        setInstallmentConfig(null);
        setSelectedInstallments(1);
        setInstallmentDates([]);
        return;
      }

      try {
        const config = await buscarConfiguracao(paymentMethod);
        setInstallmentConfig(config);
        setSelectedInstallments(1);
        // Resetar datas se não houver dueDate definida
        if (!dueDate) {
          setInstallmentDates([]);
        }
      } catch (error) {
        console.error("Erro ao carregar configuração de parcelamento:", error);
        setInstallmentConfig(null);
      }
    };
    loadInstallmentConfig();
  }, [paymentMethod, dueDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 🛡️ Proteger contra múltiplas submissões simultâneas
    if (isSubmitting) {
      console.warn("⚠️ [EditarPedido] Tentativa de submissão duplicada detectada, ignorando...");
      return;
    }

    if (!client || !client.trim()) {
      toast.error("Nome do cliente é obrigatório");
      return;
    }
    if (!type || !type.trim()) {
      toast.error("Tipo do pedido é obrigatório");
      return;
    }
    if (value <= 0) {
      toast.error("Valor deve ser maior que zero");
      return;
    }

    setIsSubmitting(true);

    try {
      let fileUrl = currentFileUrl;

      // Upload do arquivo se houver um novo
      if (file) {
        const uploadResult = await uploadOrderFile(file, id!);
        if (uploadResult.ok) {
          fileUrl = uploadResult.url;
        } else {
          toast.warning("Arquivo não foi enviado, mas o pedido será atualizado");
        }
      }

      // Preparar produtos para baixa de estoque (apenas os novos adicionados)
      const productsForInventory = addedCatalogProducts.map(product => ({
        id: product.id,
        quantity: product.quantity
      }));

      // Sempre enviar as personalizations atuais para garantir que duplicatas sejam removidas
      const personalizationsToSend = personalizations
        .filter((p) => p.personName.trim())
        .map((p) => ({
          person_name: p.personName.trim(),
          size: p.size?.trim() || undefined,
          quantity: p.quantity ?? 1,
          notes: p.notes?.trim() || undefined,
        }));

      console.log("📤 [EditarPedido] Enviando update com personalizations:", {
        count: personalizationsToSend.length || 0,
        details: personalizationsToSend.map(p => `${p.person_name} (${p.size || 'sem tamanho'}) x${p.quantity}`),
        timestamp: new Date().toISOString(),
      });

      console.log("📤 [EditarPedido] Payload COMPLETO sendo enviado para updateOrder:", {
        orderId: id,
        hasFile: !!fileUrl,
        fileUrl: fileUrl,
        personalizationsCount: personalizationsToSend.length,
      });

      const baseDescription = description
        .split("\n")
        .filter((line) => {
          const trimmed = line.trim();
          return !/^Kit:/i.test(trimmed) && !/^Total:/i.test(trimmed) && !/^Qtd:/i.test(trimmed);
        })
        .join("\n")
        .trim();

      let finalDescription = baseDescription;

      if (addedCatalogProducts.length > 0) {
        const sizeInfo = size ? ` | Tamanho: ${size}` : "";
        const colorInfo = color ? ` | Cor: ${color}` : "";
        if (sizeInfo || colorInfo) {
          finalDescription = `${finalDescription}${sizeInfo}${colorInfo}`;
        }
      }

      if (isKitMode) {
        const kitInfo = kitItems
          .filter((item) => item.quantity > 0)
          .map((item) => `${item.quantity} ${item.size}`)
          .join(" | ");
        finalDescription = `${finalDescription}\nKit: ${kitInfo}\nTotal: ${getTotalKitQuantity()} peças${color ? ` | Cor: ${color}` : ""}`;
      } else if (addedCatalogProducts.length === 0) {
        const sizeInfo = size ? ` | Tamanho: ${size}` : "";
        const colorInfo = color ? ` | Cor: ${color}` : "";
        finalDescription = `${finalDescription}\nQtd: ${quantity}${sizeInfo}${colorInfo}`;
      }

      // Construir observações com condições de pagamento
      let observationsText = (order?.observations || "").replace(/📋 CONDIÇÕES DE PAGAMENTO:[\s\S]*?(?:\n\n|$)/, "").trim();
      if (paymentConditions.trim()) {
        observationsText = observationsText
          ? `${observationsText}\n\n📋 CONDIÇÕES DE PAGAMENTO:\n${paymentConditions}`
          : `📋 CONDIÇÕES DE PAGAMENTO:\n${paymentConditions}`;
      }

      console.log("📤 [EditarPedido] ANTES DE SALVAR - Products:", {
        addedCatalogProductsCount: addedCatalogProducts.length,
        addedCatalogProductsNames: addedCatalogProducts.map(p => p.name),
        value: value,
      });

      const result = await updateOrder(id!, {
        customer_name: client,
        customer_phone: order?.customer_phone || "",
        customer_email: order?.customer_email || "",
        type: type,
        description: finalDescription,
        value: value,
        discount: discount,
        discount_type: discountType,
        paid: paid,
        delivery_date: delivery,
        due_date: dueDate,
        payment_method: paymentMethod || undefined,
        status: status,
        observations: observationsText || undefined,
        file_url: fileUrl,
        catalog_products: addedCatalogProducts as any,
        personalizations: personalizationsToSend,
        products: productsForInventory.length > 0 ? productsForInventory : undefined,
      });

      console.log("📥 [EditarPedido] Resposta do updateOrder:", {
        ok: result.ok,
        error: result.error,
        personalizationsAfterUpdate: result.data?.personalizations?.length || 0,
        catalogProductsAfterSave: result.data?.catalog_products?.length || 0,
      });

      if (result.ok) {
        // Recarregar artes para garantir que estão sincronizadas
        const arquivosAtualizados = await getArquivos('orders', id!);
        setArquivos(arquivosAtualizados);

        toast.success("Pedido atualizado com sucesso!");
        // Sincronização automática
        syncAfterUpdate('orders', id!, result.data);
        invalidateRelated('orders');
        navigate(`/pedidos/${id}`);
      } else {
        toast.error(result.error || "Erro ao atualizar pedido");
      }
    } catch (error) {
      console.error("Erro ao atualizar pedido:", error);
      toast.error("Erro ao atualizar pedido");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center gap-4 p-4">
            <SidebarTrigger />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/pedidos")}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Pedido não encontrado</h1>
            </div>
          </div>
        </header>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="flex items-center gap-4 p-4">
          <SidebarTrigger />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/pedidos")}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Editar Pedido</h1>
            <p className="text-sm text-muted-foreground">Pedido #{order.code}</p>
          </div>
        </div>
      </header>

      <div className="p-6 max-w-4xl mx-auto">
        <form onSubmit={handleSubmit}>
          <Card className="border-border animate-fade-in">
            <CardHeader>
              <CardTitle className="text-foreground">Informações do Pedido</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client">Cliente *</Label>
                  <Input
                    id="client"
                    placeholder="Nome do cliente"
                    required
                    className="border-input"
                    value={client}
                    onChange={(e) => setClient(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Tipo de Produto *</Label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="border-input">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bordado">Bordado</SelectItem>
                      <SelectItem value="camiseta">Camiseta</SelectItem>
                      <SelectItem value="uniforme">Uniforme</SelectItem>
                      <SelectItem value="personalizado">Personalizado</SelectItem>
                      <SelectItem value="catalogo">Item do Catálogo</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Seletor de Catálogo - Sempre visível */}
              {type === "catalogo" && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-3">
                    <Package className="w-4 h-4 text-blue-600" />
                    <Label className="text-blue-800 font-medium">Adicionar do Catálogo</Label>
                  </div>
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <Popover open={productPopoverOpen} onOpenChange={setProductPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={productPopoverOpen}
                            className="flex-1 justify-between"
                          >
                            {selectedProduct
                              ? products.find((product) => product.id === selectedProduct)?.name || "Selecione um produto"
                              : "Selecione um produto do catálogo"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        {selectedProduct && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedProduct("");
                              setProductSearchTerm("");
                            }}
                            className="shrink-0"
                            title="Remover seleção"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                        <PopoverContent className="w-[400px] p-0" align="start">
                          <Command>
                            <CommandInput 
                              placeholder="Pesquisar produto por nome, tipo ou material..." 
                              value={productSearchTerm}
                              onValueChange={setProductSearchTerm}
                            />
                            <CommandList>
                              <CommandEmpty>Nenhum produto encontrado.</CommandEmpty>
                              <CommandGroup>
                                {filteredProducts.map((product) => (
                                  <CommandItem
                                    key={product.id}
                                    value={`${product.name} ${product.type || ""} ${product.materials?.join(" ") || ""}`}
                                    onSelect={() => {
                                      setSelectedProduct(product.id);
                                      setProductPopoverOpen(false);
                                      setProductSearchTerm("");
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        selectedProduct === product.id ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    <div className="flex flex-col">
                                      <span className="font-medium">{product.name}</span>
                                      <span className="text-xs text-muted-foreground">
                                        R$ {product.unit_price.toFixed(2)}
                                        {product.type && ` • ${product.type}`}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <Button
                        type="button"
                        onClick={() => {
                          if (!selectedProduct) {
                            toast.error("Selecione um produto");
                            return;
                          }
                          const product = products.find(p => p.id === selectedProduct);
                          if (product) {
                            const productLine = `${product.name}${product.type ? ` - ${product.type}` : ""}${product.materials && product.materials.length > 0 ? ` (${product.materials.join(", ")})` : ""} | Qtd: 1`;
                            setDescription(prev => prev ? `${prev}\n${productLine}` : productLine);
                            
                            setAddedCatalogProducts(prev => [...prev, {
                              id: product.id,
                              name: product.name,
                              price: product.unit_price,
                              quantity: 1,
                              fiscal_data: product.fiscal_data // ✅ NOVO: Incluir dados fiscais do produto
                            }]);
                            
                            setSelectedProduct("");
                            toast.success("Produto adicionado!");
                          }
                        }}
                        disabled={!selectedProduct}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar
                      </Button>
                    </div>
                    {addedCatalogProducts.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <Label className="text-sm text-blue-700 font-medium">Produtos adicionados:</Label>
                        <div className="space-y-2">
                          {addedCatalogProducts.map((addedProduct, index) => {
                            const product = products.find(p => p.id === addedProduct.id);
                            return (
                              <div
                                key={`${addedProduct.id}-${index}`}
                                className="space-y-2 p-3 bg-white border border-blue-200 rounded-md"
                              >
                                {/* Header com nome, preço, qtd e remover */}
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{addedProduct.name}</p>
                                    <p className="text-xs text-gray-500">
                                      R$ {addedProduct.price.toFixed(2).replace(".", ",")}
                                      {product?.type && ` • ${product.type}`}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Label htmlFor={`qty-${index}`} className="text-xs text-gray-600 whitespace-nowrap">
                                      Qtd:
                                    </Label>
                                    <Input
                                      id={`qty-${index}`}
                                      type="number"
                                      min="1"
                                      value={addedProduct.quantity}
                                      onChange={(e) => {
                                        const newQuantity = Math.max(1, parseInt(e.target.value) || 1);
                                        setAddedCatalogProducts(prev => prev.map((p, i) =>
                                          i === index ? { ...p, quantity: newQuantity } : p
                                        ));
                                        const lines = description.split('\n');
                                        const updatedLines = lines.map(line => {
                                          if (line.includes(addedProduct.name)) {
                                            return line.replace(/Qtd: \d+/, `Qtd: ${newQuantity}`);
                                          }
                                          return line;
                                        });
                                        setDescription(updatedLines.join('\n'));
                                      }}
                                      className="w-16 h-8 text-sm"
                                    />
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => {
                                      setAddedCatalogProducts(prev => prev.filter((p, i) => i !== index));
                                      const lines = description.split('\n');
                                      const filteredLines = lines.filter(line => !line.includes(addedProduct.name));
                                      setDescription(filteredLines.join('\n').trim());
                                    }}
                                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    title="Remover produto"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>

                                {/* Especificações editáveis */}
                                {addedProduct.especificacoes && typeof addedProduct.especificacoes === 'object' && Object.keys(addedProduct.especificacoes).length > 0 && (
                                  <div className="border-t pt-2 space-y-2">
                                    <p className="text-xs font-semibold text-gray-700">Especificações:</p>
                                    {Object.entries(addedProduct.especificacoes)
                                      .filter(([key]) => !key.toLowerCase().includes('id'))
                                      .map(([key, value]) => (
                                        <div key={key} className="flex items-center gap-2">
                                          <Label htmlFor={`spec-${index}-${key}`} className="text-xs text-gray-600 min-w-fit">
                                            {key.charAt(0).toUpperCase() + key.slice(1)}:
                                          </Label>
                                          <Input
                                            id={`spec-${index}-${key}`}
                                            type="text"
                                            value={String(value || '')}
                                            onChange={(e) => {
                                              setAddedCatalogProducts(prev => prev.map((p, i) =>
                                                i === index
                                                  ? { ...p, especificacoes: { ...p.especificacoes, [key]: e.target.value } }
                                                  : p
                                              ));
                                            }}
                                            className="h-7 text-xs flex-1"
                                            placeholder={`${key}...`}
                                          />
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Descrição <span className="text-gray-400">(opcional)</span></Label>
                <Textarea
                  id="description"
                  placeholder="Descreva detalhadamente o produto..."
                  rows={4}
                  className="border-input resize-none"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  title="💡 Opcional - A descrição do produto aparecerá automaticamente"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="qty">Quantidade</Label>
                  <Input
                    id="qty"
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    disabled={addedCatalogProducts.length > 0}
                    title={addedCatalogProducts.length > 0 ? "Quantidade controlada pelos produtos do catálogo" : ""}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="size">Tamanho</Label>
                    <AddCustomSizeDialog
                      onAddSize={addCustomSize}
                      isLoading={isSizesLoading}
                      trigger={
                        <button
                          type="button"
                          className="text-xs px-2 py-1 border border-input rounded hover:bg-accent text-muted-foreground"
                          title="Adicionar novo tamanho"
                        >
                          + Novo
                        </button>
                      }
                    />
                  </div>
                  <Select value={size} onValueChange={setSize}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tamanho" />
                    </SelectTrigger>
                    <SelectContent>
                      {clothingSizes.map((sizeOption) => (
                        <SelectItem key={sizeOption.value} value={sizeOption.value}>
                          {sizeOption.label}{sizeOption.isCustom ? " (Custom)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="color">Cor</Label>
                  <Input
                    id="color"
                    placeholder="Ex.: Preto, Azul..."
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                  />
                </div>
              </div>

              {/* Controle de Kits e Tamanhos */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Controle de Kits e Tamanhos</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsKitMode(!isKitMode)}
                    className="text-xs"
                  >
                    {isKitMode ? "Modo Simples" : "Modo Kit"}
                  </Button>
                </div>

                {isKitMode ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Ideal para uniformes e lotes com diferentes tamanhos
                    </p>
                    <div className="space-y-2">
                      {kitItems.map((item) => (
                        <div key={item.id} className="flex items-center gap-2">
                          <Input
                            placeholder="Tamanho (1 ano, 2 anos, P, M, G...)"
                            value={item.size}
                            onChange={(e) => updateKitSize(item.id, e.target.value)}
                            className="w-24"
                          />
                          <Input
                            type="number"
                            min="0"
                            placeholder="Qtd"
                            value={item.quantity}
                            onChange={(e) => updateKitQuantity(item.id, Number(e.target.value))}
                            className="w-20"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeKitSize(item.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addKitSize}
                        className="w-full"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Tamanho
                      </Button>
                    </div>
                    <div className="p-3 bg-muted/50 rounded-lg">
                      <p className="text-sm font-medium">
                        Total do Kit: {getTotalKitQuantity()} peças
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {kitItems
                          .filter((item) => item.quantity > 0)
                          .map((item) => `${item.quantity} ${item.size}`)
                          .join(" | ")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      Quantidade simples: {quantity} peça{quantity !== 1 ? "s" : ""}
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="value">Valor Total (R$) *</Label>
                  <Input
                    id="value"
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    required
                    className="border-input"
                    value={value}
                    onChange={(e) => setValue(Number(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="paid">Valor Pago (R$)</Label>
                  <Input
                    id="paid"
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    className="border-input"
                    value={paid}
                    onChange={(e) => setPaid(Number(e.target.value))}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="delivery">Data de Entrega</Label>
                  <Input
                    id="delivery"
                    type="date"
                    className="border-input"
                    value={delivery}
                    onChange={(e) => setDelivery(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dueDate">📅 Data de Vencimento (Pagamento)</Label>
                  <Input
                    id="dueDate"
                    type="date"
                    className="border-input"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">Quando o cliente deve pagar. Se vazio, usa a data de entrega.</p>
                </div>
              </div>

              <Card className="border border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 shadow-sm">
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {/* Título compacto */}
                    <div className="flex items-center gap-2 pb-2 border-b border-purple-200">
                      <span className="text-lg">💳</span>
                      <h3 className="font-semibold text-purple-900 text-sm">Pagamento e Vencimento</h3>
                    </div>

                    {/* Campos em linha única */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {/* Forma de Pagamento */}
                      <div>
                        <Label htmlFor="paymentMethod" className="text-xs font-semibold text-gray-700">
                          Forma <span className="text-red-500">*</span>
                        </Label>
                        <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                          <SelectTrigger className="border border-purple-200 bg-white h-9 text-xs mt-1 hover:border-purple-300">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Dinheiro">💵 Dinheiro</SelectItem>
                            <SelectItem value="Cartão de Crédito">💳 Cartão de Crédito</SelectItem>
                            <SelectItem value="Cartão de Débito">🏦 Cartão de Débito</SelectItem>
                            <SelectItem value="PIX">📱 PIX</SelectItem>
                            <SelectItem value="Cheque">🏛️ Cheque</SelectItem>
                            <SelectItem value="Transferência">🏦 Transferência</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Data de Vencimento */}
                      <div>
                        <Label htmlFor="dueDate" className="text-xs font-semibold text-gray-700">
                          Vencimento
                        </Label>
                        <Input
                          id="dueDate"
                          type="date"
                          value={dueDate}
                          onChange={(e) => setDueDate(e.target.value)}
                          className="border border-purple-200 bg-white h-9 text-xs mt-1 hover:border-purple-300"
                        />
                      </div>

                      {/* Taxa de Cartão - Compacta */}
                      {(paymentMethod.includes('Cartão') || !paymentMethod) && (
                        <div>
                          <Label htmlFor="cardFee" className="text-xs font-semibold text-gray-700">
                            Taxa Cartão (%)
                          </Label>
                          <div className="relative mt-1">
                            <Input
                              id="cardFee"
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={cardFeePercent}
                              onChange={(e) => setCardFeePercent(parseFloat(e.target.value) || 0)}
                              placeholder="2.99"
                              className="border border-orange-200 bg-white h-9 text-xs pr-6 hover:border-orange-300"
                            />
                            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">%</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Mostrar desconto inline se houver taxa */}
                    {cardFeePercent > 0 && (
                      <div className="text-xs text-orange-700 bg-orange-50 px-2 py-1 rounded border border-orange-200">
                        Desconto da taxa: -R$ {(value * (cardFeePercent / 100)).toFixed(2)}
                      </div>
                    )}

                    {/* Parcelamento Section */}
                    {installmentConfig && installmentConfig.max_installments > 1 && dueDate && (
                      <div className="space-y-3 pt-3 border-t border-gray-200">
                        <Label className="text-xs font-semibold">Parcelamento</Label>
                        <div className="flex flex-wrap gap-2">
                          {Array.from({ length: installmentConfig.max_installments }, (_, i) => i + 1).map(
                            (installments) => (
                              <Button
                                key={installments}
                                type="button"
                                variant={selectedInstallments === installments ? "default" : "outline"}
                                size="sm"
                                onClick={() => {
                                  setSelectedInstallments(installments);
                                  const dates = gerarDatasParcelamento(
                                    dueDate,
                                    installments,
                                    installmentConfig.days_between_installments
                                  );
                                  setInstallmentDates(dates);
                                }}
                                className="w-20 h-8 text-xs"
                              >
                                {installments}x
                              </Button>
                            )
                          )}
                        </div>

                        {/* Mostrar datas de parcelamento */}
                        {installmentDates.length > 0 && (
                          <div className="p-3 bg-blue-50 rounded border border-blue-200 space-y-2">
                            <p className="text-xs font-semibold text-blue-900">
                              📅 Datas de Vencimento ({selectedInstallments}x)
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                              {installmentDates.map((date, idx) => (
                                <div
                                  key={idx}
                                  className="text-xs bg-white p-2 rounded border border-blue-200 text-center"
                                >
                                  <div className="font-semibold text-blue-900">Parcela {idx + 1}</div>
                                  <div className="text-blue-700">
                                    {new Date(date).toLocaleDateString("pt-BR")}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="discountType">Tipo de Desconto</Label>
                  <Select value={discountType} onValueChange={(val) => setDiscountType(val as 'fixed' | 'percentage')}>
                    <SelectTrigger className="border-input">
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                      <SelectItem value="percentage">Percentual (%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="discount">Valor do Desconto</Label>
                  <Input
                    id="discount"
                    type="number"
                    step="0.01"
                    placeholder="0,00"
                    className="border-input"
                    value={discount}
                    onChange={(e) => setDiscount(Number(e.target.value))}
                  />
                </div>
              </div>

              {(discount > 0 || cardFeePercent > 0) && (
                <>
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-xs text-muted-foreground font-semibold">Subtotal</span>
                        <span className="text-lg font-bold text-foreground">R$ {value.toFixed(2)}</span>
                      </div>
                      {discount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground font-semibold">
                            {discountType === 'percentage' ? 'Desconto (%)' : 'Desconto (R$)'}
                          </span>
                          <span className="text-lg font-bold text-red-600">
                            -{discountType === 'percentage' ? `${discount.toFixed(2)}%` : `R$ ${discount.toFixed(2)}`}
                          </span>
                        </div>
                      )}
                      {cardFeePercent > 0 && (
                        <div className="flex justify-between">
                          <span className="text-xs text-muted-foreground font-semibold">Taxa de Cartão ({cardFeePercent}%)</span>
                          <span className="text-lg font-bold text-red-600">
                            -R$ {(value * (cardFeePercent / 100)).toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="border-t pt-3 flex justify-between">
                        <span className="text-xs text-muted-foreground font-semibold">VALOR FINAL</span>
                        <span className="text-lg font-bold text-green-600">
                          R$ {(
                            (discountType === 'percentage'
                              ? value * (1 - discount / 100)
                              : Math.max(0, value - discount)) -
                            (value * (cardFeePercent / 100))
                          ).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="finalValue">Valor Final (Com Desconto e Taxa) - R$</Label>
                    <Input
                      id="finalValue"
                      type="number"
                      step="0.01"
                      readOnly
                      className="border-input bg-green-50"
                      value={(
                        (discountType === 'percentage'
                          ? value * (1 - discount / 100)
                          : Math.max(0, value - discount)) -
                        (value * (cardFeePercent / 100))
                      ).toFixed(2)}
                    />
                  </div>
                </>
              )}

              {discount > 0 && (
                <>
                  <div style={{display: 'none'}}>
                    <Input
                      id="finalValue"
                      type="number"
                      step="0.01"
                      readOnly
                      className="border-input bg-green-50"
                      value={(discountType === 'percentage'
                        ? value * (1 - discount / 100)
                        : Math.max(0, value - discount)
                      ).toFixed(2)}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="border-input">
                    <SelectValue placeholder="Selecione o status" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {id && (
                <ArquivosSection
                  tableType="orders"
                  recordId={id}
                  arquivos={arquivos}
                  onArquivosUpdated={setArquivos}
                  isExpanded={true}
                />
              )}

              <PersonalizationListEditor
                entries={personalizations}
                onChange={setPersonalizations}
                showNotes
                description="Atualize aqui os nomes, tamanhos e observações de cada peça personalizada deste pedido."
                availableSizes={clothingSizes}
                onAddSize={addCustomSize}
                isLoadingSizes={isSizesLoading}
              />

              <div className="flex flex-col-reverse gap-3 pt-4 border-t border-border sm:flex-row sm:items-center">
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90 flex-1 w-full sm:w-auto"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar Alterações
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/pedidos")}
                  className="border-border w-full sm:w-auto"
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
}


